<template>
  <div>
    <!-- Alert when viewing transfer outside current week -->
    <div v-if="hasNoRelevantData" class="alert alert-info mb-2">
      <strong>{{ __DICT__.loadTransferView.limitedData }}</strong> {{ __DICT__.transferInfo.outsideWeekWarning }}
    </div>

    <!-- OTA Transfer Indicator -->
    <div v-if="isOtaTransfer" class="alert alert-warning mb-2">
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      <strong>{{ __DICT__.loadTransferView.otaTransfer }}</strong> - {{ __DICT__.transferInfo.allExternalPassengers }}
    </div>

    <!-- Capacity Summary -->
    <div class="mb-2">
      <div class="accordion" id="detailsAccordion">
        <AccordionItem id="detailsCollapse" title="Capacity Summary" parent-id="detailsAccordion">
          <TransferPassengers
              :direction="direction.toString()"
              :week-data="weekData"
              :transfer-data="transferData"
              :transfer-id="transferId.toString()"
              :staff="reps"
          />
        </AccordionItem>
      </div>
    </div>

    <!-- Pick Up Locations Section -->
    <div class="mb-2">
      <div class="accordion" id="pickupAccordion">
        <AccordionItem id="pickupCollapse" title="Scanning progress by pick up" parent-id="pickupAccordion">
          <div v-if="hasNoRelevantData" class="text-muted small p-2">
            {{ __DICT__.transferInfo.pickupBreakdownNotAvailable }}
          </div>
          <div class="mb-3" v-for="p in passengers" :key="`pickup-${p.location}`">
            <div class="accordion" :id="`pickup-location-${sanitizeForSelector(p.location)}`">
              <AccordionItem
                  :id="`pickup-collapse-${sanitizeForSelector(p.location)}`"
                  :parent-id="`pickup-location-${sanitizeForSelector(p.location)}`"
              >
                <template #header>
                  <div class="w-100 pe-2">
                    <ProgressBar
                        :percent="(p.loadedPassengers.length / (p.loadedPassengers.length + p.remainingPassengers.length)) * 100"
                        :total="p.loadedPassengers.length + p.remainingPassengers.length"
                        :raw="p.loadedPassengers.length"
                        :heading="p.location"
                    />
                  </div>
                </template>
                <div class="accordion" :id="`pickup-status-${sanitizeForSelector(p.location)}`">
                  <AccordionItem
                      :id="`pickup-remaining-${sanitizeForSelector(p.location)}`"
                      :parent-id="`pickup-status-${sanitizeForSelector(p.location)}`"
                      :title="`Remaining Passengers (${p.remainingPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.remainingPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        <Badge v-if="passenger.equipment" text="E" pill/>
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0] === 'P24' ? 'Other' : passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'black', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                  <AccordionItem
                      :id="`pickup-loaded-${sanitizeForSelector(p.location)}`"
                      :parent-id="`pickup-status-${sanitizeForSelector(p.location)}`"
                      :title="`Loaded Passengers (${p.loadedPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.loadedPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        <Badge v-if="passenger.equipment" text="E" pill/>
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0] === 'P24' ? 'Other' : passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'black', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                </div>
              </AccordionItem>
            </div>
          </div>
        </AccordionItem>
      </div>
    </div>

    <!-- Expected Passengers -->
    <div class="mb-3">
      <div class="accordion" id="mainAccordion">
        <AccordionItem id="mainCollapse" parent-id="mainAccordion">
          <template #header>
            {{ __DICT__.expected }} {{ __DICT__.passengers }} <br/>
            <div class="w-100 pe-2">
              <ProgressBar
                  :percent="(ticketData.loaded/(remainingPassengers.length + loadedPassengers.length))*100"
                  :total="remainingPassengers.length + loadedPassengers.length"
                  :raw="ticketData.loaded"
                  heading="Loaded"
              />
              <div class="mt-2"></div>
              <ProgressBar
                  :percent="(loadedEquipment/(remainingEquipment + loadedEquipment))*100"
                  :total="remainingEquipment + loadedEquipment"
                  :raw="loadedEquipment"
                  heading="Equipment"
              />
            </div>
          </template>
          <div v-if="hasNoRelevantData" class="alert alert-warning mb-3 small">
            {{ __DICT__.transferInfo.equipmentNotAvailable }}
          </div>
          <Card class="mb-3" :title="`Remaining Passengers (${remainingPassengers.length})`" :title-level="3" margin="">
            <ul class="list-group list-group-flush">
              <li v-for="passenger in remainingPassengers"
                  :key="passenger.bookingRef"
                  class="list-group-item d-flex justify-content-between"
                  @click="$emit('openPassengerQuickView', passenger)"
              >
                <Badge v-if="passenger.equipment" text="E" pill/>
                {{ passenger.name }}
                <Badge
                    :text="passenger.bookingRef?.split('-')[0] === 'P24' ? 'OTHER' : passenger.bookingRef?.split('-')[0]"
                    :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'black', 'P24': 'purple-lt' }[passenger.bookingRef?.split('-')[0]] ?? 'orange'"
                    class="ms-auto"
                />
              </li>
            </ul>
          </Card>
          <Card class="mb-3" :title="`Loaded Passengers (${loadedPassengers.length})`" :title-level="3" margin="">
            <ul class="list-group list-group-flush">
              <li v-for="passenger in loadedPassengers"
                  :key="passenger.bookingRef"
                  class="list-group-item d-flex justify-content-between"
                  @click="$emit('openPassengerQuickView', passenger)"
              >
                <Badge v-if="passenger.equipment" text="E" pill/>
                {{ passenger.name }}
                <Badge
                    :text="passenger.bookingRef?.split('-')[0] === 'P24' ? 'Other' : passenger.bookingRef?.split('-')[0]"
                    :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'grey-500', 'P24': 'purple-lt' }[passenger.bookingRef?.split('-')[0]] ?? 'orange'"
                    class="ms-auto"
                />
              </li>
            </ul>
          </Card>
        </AccordionItem>
      </div>
    </div>

    <!-- Reps & Welfare -->
    <div class="accordion" id="repsAccordion">
      <AccordionItem :title="`Reps & Welfare (${reps.length})`" show-chevron>
        <div class="list-group list-group-flush">
          <div v-if="hasNoRelevantData && reps.length === 0" class="list-group-item text-muted small">
            {{ __DICT__.transferInfo.repDataNotAvailable }}
          </div>
          <div @click="$emit('openRepQuickView', rep)" v-for="rep in reps" :key="rep['GBS ID'] || rep.Name"
               class="list-group-item list-group-item-action d-flex justify-content-between">
            {{ rep.Name }}<br/>
            <span class="small">{{ repPickup(rep) || rep.Resort }}</span>
          </div>
        </div>
      </AccordionItem>
    </div>

    <!-- Additional Passengers -->
    <div class="accordion mt-2" id="additionalPaxAccordion">
      <AccordionItem :title="`${__DICT__.loadTransferView.additional} ${__DICT__.passengers} (${additionalPax.length})`" show-chevron>
        <div class="list-group list-group-flush">
          <div v-if="hasNoRelevantData && additionalPax.length > 0" class="list-group-item text-muted small">
            {{ __DICT__.transferInfo.passengerNamesNotAvailable }}
          </div>
          <div v-for="pax in additionalPax" :key="pax.bookingRef"
               class="list-group-item d-flex justify-content-between">
            {{ getPassengerFromBookingRef(pax.bookingRef)?.passengerName || pax.bookingRef }}
          </div>
        </div>
      </AccordionItem>
    </div>

    <!-- Total Passengers -->
    <div class="accordion mt-2" id="totalPaxAccordion">
      <AccordionItem :title="`Total ${__DICT__.passengers} (${(additionalPax.length + loadedPassengers.length)})`" show-chevron>
        <div class="list-group list-group-flush">
          <div class="list-group-item d-flex justify-content-between">{{ __DICT__.passengers }}: {{ loadedPassengers.length }}</div>
          <div class="list-group-item d-flex justify-content-between">{{ __DICT__.loadTransferView.additional }} {{ __DICT__.passengers }}: {{ additionalPax.length }}</div>
        </div>
      </AccordionItem>
    </div>

    <!-- Drop Off Locations Section -->
    <div class="mt-2">
      <div class="accordion" id="dropoffAccordion">
        <AccordionItem id="dropoffCollapse" :title="`${__DICT__.passenger} drop off`" parent-id="dropoffAccordion">
          <div v-if="hasNoRelevantData" class="text-muted small p-2">
            {{ __DICT__.transferInfo.dropoffBreakdownNotAvailable }}
          </div>
          <div class="mb-3" v-for="p in dropOffPassengers" :key="`dropoff-${p.location}`">
            <div class="accordion" :id="`dropoff-location-${sanitizeForSelector(p.location)}`">
              <AccordionItem
                  :id="`dropoff-collapse-${sanitizeForSelector(p.location)}`"
                  :parent-id="`dropoff-location-${sanitizeForSelector(p.location)}`"
              >
                <template #header>
                  <div class="w-100 pe-2">
                    <ProgressBar
                        :percent="(p.loadedPassengers.length / (p.loadedPassengers.length + p.remainingPassengers.length)) * 100"
                        :total="p.loadedPassengers.length + p.remainingPassengers.length"
                        :raw="p.loadedPassengers.length"
                        :heading="p.location"
                    />
                  </div>
                </template>
                <div class="accordion" :id="`dropoff-status-${sanitizeForSelector(p.location)}`">
                  <AccordionItem
                      :id="`dropoff-remaining-${sanitizeForSelector(p.location)}`"
                      :parent-id="`dropoff-status-${sanitizeForSelector(p.location)}`"
                      :title="`Remaining Passengers (${p.remainingPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.remainingPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        <Badge v-if="passenger.equipment" text="E" pill/>
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0] === 'P24' ? 'Other' : passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'grey-500', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                  <AccordionItem
                      :id="`dropoff-loaded-${sanitizeForSelector(p.location)}`"
                      :parent-id="`dropoff-status-${sanitizeForSelector(p.location)}`"
                      :title="`Loaded Passengers (${p.loadedPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.loadedPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        <Badge v-if="passenger.equipment" text="E" pill/>
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0] === 'P24' ? 'Other' : passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'grey-500', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                </div>
              </AccordionItem>
            </div>
          </div>
        </AccordionItem>
      </div>
    </div>
    <div style="height: 100px"></div>
  </div>
</template>

<script setup>
import ProgressBar from "@/components/ProgressBar.vue";
import TransferPassengers from "@/components/TransferPassengers.vue";
import {AccordionItem, Badge, Card} from "@/components/atomic";
import {sanitizeForSelector} from "@/utils/cssUtils.js";
import {passengerIndex} from "@/services/PassengerIndex.js";

const props = defineProps({
  direction: { type: String, required: true },
  weekData: { type: Array, default: () => [] },
  transferData: { type: Object, default: null },
  transferId: { type: String, required: true },
  reps: { type: Array, default: () => [] },
  passengers: { type: Array, default: () => [] },
  remainingPassengers: { type: Array, default: () => [] },
  loadedPassengers: { type: Array, default: () => [] },
  dropOffPassengers: { type: Array, default: () => [] },
  ticketData: { type: Object, default: () => ({}) },
  additionalPax: { type: Array, default: () => [] },
  hasNoRelevantData: { type: Boolean, default: false },
  isOtaTransfer: { type: Boolean, default: false },
  remainingEquipment: { type: Number, default: 0 },
  loadedEquipment: { type: Number, default: 0 },
});

defineEmits(['openPassengerQuickView', 'openRepQuickView']);

const repPickup = (rep) => {
  if (props.direction === 'outbound') {
    return rep['OB Pickup Point']
  } else {
    return rep['IB Pickup Point']
  }
};

const getPassengerFromBookingRef = (bookingRef) => {
  return passengerIndex.getByReference(bookingRef)
};
</script>
